import React, { useRef } from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { resolveImageUrl } from '../../services/api';
import { Camera, UploadSimple, X } from '@phosphor-icons/react';

export function ProductImageUpload({
  formData, setFormData, previewUrl, setPreviewUrl,
  uploading, onFileUpload, showAvatarPicker, setShowAvatarPicker,
  onSelectAvatar, avatarsForCategory, fileInputRef
}) {
  const removeImage = (url) => {
    const newImages = (formData.images || []).filter(i => i !== url);
    setFormData(prev => ({
      ...prev,
      image_url: url === prev.image_url ? (newImages[0] || '') : prev.image_url,
      images: newImages
    }));
    if (url === previewUrl) setPreviewUrl(newImages[0] || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Product Image</Label>
      
      <div className="flex gap-4 items-start">
        <div className="relative group">
          {previewUrl ? (
            <div className="w-24 h-24 rounded-sm overflow-hidden border-2 border-[#22C55E] relative">
              <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" data-testid="image-preview" />
              <button
                type="button"
                onClick={() => removeImage(previewUrl)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF3B30] rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                data-testid="remove-image-button"
              >
                <X className="w-3 h-3" weight="bold" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-24 h-24 rounded-sm border-2 border-dashed border-[#333333] hover:border-[#22C55E] flex flex-col items-center justify-center text-[#999999] hover:text-[#22C55E] transition-colors"
              data-testid="upload-trigger-button"
            >
              {uploading ? (
                <div className="animate-spin w-6 h-6 border-2 border-[#22C55E] border-t-transparent rounded-full" />
              ) : (
                <>
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs">Upload</span>
                </>
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFileUpload}
            className="hidden"
            data-testid="file-input"
          />
        </div>

        <div className="flex-1">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="rounded-sm border-[#333333] hover:bg-[#333333] text-white text-xs" data-testid="upload-photo-button">
              <UploadSimple className="w-3 h-3 mr-1" />
              {formData.images?.length > 0 ? 'Add More' : 'Upload Photo'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAvatarPicker(!showAvatarPicker)} className="rounded-sm border-[#333333] hover:bg-[#333333] text-[#22C55E] text-xs" data-testid="pick-avatar-button">
              Quick Avatar
            </Button>
          </div>
          <p className="text-xs text-[#666666] mt-2">JPEG, PNG, WebP, GIF. Max 5MB. Up to 5 images.</p>
        </div>
      </div>

      {formData.images?.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="image-thumbnails">
          {formData.images.map((img) => (
            <div key={img} className={`relative w-14 h-14 rounded-sm overflow-hidden border-2 cursor-pointer ${previewUrl === img ? 'border-[#22C55E]' : 'border-[#333333]'}`} onClick={() => setPreviewUrl(img)}>
              <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeImage(img); }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF3B30] rounded-full flex items-center justify-center text-white text-[8px]"
              >
                <X className="w-2 h-2" weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAvatarPicker && (
        <div className="bg-[#111111] border border-[#333333] rounded-sm p-4 space-y-3" data-testid="avatar-picker">
          <p className="text-xs font-semibold text-[#999999] uppercase tracking-wider">
            {formData.category ? `${formData.category} Avatars` : 'Quick Avatars'}
          </p>
          <div className="grid grid-cols-6 gap-2">
            {avatarsForCategory.map((avatar) => (
              <button
                key={avatar.id}
                type="button"
                onClick={() => onSelectAvatar(avatar.id)}
                className="w-full aspect-square rounded-sm overflow-hidden border-2 border-transparent hover:border-[#22C55E] transition-colors cursor-pointer"
                data-testid={`avatar-option-${avatar.id}`}
              >
                <div id={`avatar-${avatar.id}`}>{avatar.svg}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
